// components/Reusablesteps/onboarding/OTPScreen.jsx
// 6-box OTP input with countdown resend timer.
//
// FIX: each box is type="text" (not "number") so browser doesn't
// allow multi-char input or show spinners. onChange only accepts a
// single digit and clears the box before accepting a new one.

import React, { useEffect, useRef, useState } from "react";
import styles from "./Onboarding.module.css";
import { SecureLine, PrimaryBtn, ProgressDots } from "./onboardingTokens";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 42;

/**
 * OTPScreen
 *
 * @param {string}   phone         - phone number shown in subtitle
 * @param {function} onVerify      - (otpString) => void
 * @param {function} onResend      - () => void  (optional)
 * @param {number}   currentStep
 * @param {number}   totalSteps
 */
const OTPScreen = ({
  phone        = "",
  onVerify,
  onResend,
  currentStep  = 1,
  totalSteps   = 2,
}) => {
  const [otp,   setOtp]   = useState(Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const refs = useRef([]);

  /* ── Countdown ───────────────────────────────────────────── */
  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  /* ── Handlers ────────────────────────────────────────────── */
  const handleChange = (index, e) => {
    const raw = e.target.value;

    // Only take the last character typed (handles over-type)
    const digit = raw.replace(/\D/g, "").slice(-1);

    const next = [...otp];
    next[index] = digit;
    setOtp(next);

    // Auto-advance focus forward
    if (digit && index < OTP_LENGTH - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        // Clear current box
        const next = [...otp];
        next[index] = "";
        setOtp(next);
      } else if (index > 0) {
        // Move back
        refs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleFocus = (e) => {
    // Select existing content so typing replaces it
    e.target.select();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH)
      .split("");

    const next = Array(OTP_LENGTH).fill("");
    pasted.forEach((d, i) => { next[i] = d; });
    setOtp(next);

    // Focus the next empty box after paste
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    refs.current[focusIdx]?.focus();
  };

  const handleResend = () => {
    setTimer(RESEND_SECONDS);
    setOtp(Array(OTP_LENGTH).fill(""));
    refs.current[0]?.focus();
    onResend?.();
  };

  /* ── Derived ─────────────────────────────────────────────── */
  const filled    = otp.every(d => d !== "");
  const otpString = otp.join("");

  /* ── Render ──────────────────────────────────────────────── */
  return (
    <div className={styles.wrap}>
      <ProgressDots current={currentStep} total={totalSteps} />

      <div className={styles.centre}>

        {/* Heading */}
        <div style={{ textAlign: "center" }}>
          <h2 className={styles.question} style={{ marginBottom: 8 }}>
            Enter Verification Code
          </h2>
          <p className={styles.desc}>
            We sent a 6-digit OTP to{" "}
            <strong style={{ color: "#1a2e6c" }}>+91 {phone}</strong>
          </p>
        </div>

        {/* OTP boxes */}
        <div className={styles.otpBoxRow} onPaste={handlePaste}>
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={el => refs.current[i] = el}
              type="text"
              inputMode="numeric"
              pattern="\d*"
              maxLength={1}
              value={digit}
              onChange={e => handleChange(i, e)}
              onKeyDown={e => handleKeyDown(i, e)}
              onFocus={handleFocus}
              autoComplete="one-time-code"
              className={[
                styles.otpBox,
                digit          ? styles.otpBoxFilled : "",
                document.activeElement === refs.current[i] ? styles.otpBoxFocus : "",
              ].join(" ")}
            />
          ))}
        </div>

        {/* Resend */}
        <p className={styles.resendText}>
          Didn't receive it?{" "}
          {timer > 0 ? (
            <span>
              Resend in{" "}
              <strong style={{ color: "#4B2DA0" }}>{timer}s</strong>
            </span>
          ) : (
            <button className={styles.resendBtn} onClick={handleResend} type="button">
              Resend
            </button>
          )}
        </p>

        {/* Verify button */}
        <div style={{ width: "100%", maxWidth: 380, marginTop: 16 }}>
          <PrimaryBtn
            label="Verify & Confirm"
            onClick={() => onVerify?.(otpString)}
            disabled={!filled}
          />
        </div>

      </div>

      {/* <SecureLine /> */}
    </div>
  );
};

export default OTPScreen;
